import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, Mail, MessageSquare, MapPin, Star, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_MAP = {
  '/contact': 'contact',
  '/services': 'services',
  '/faq': 'faq',
  '/about': 'about',
};

export default function CMSPage() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const pageId = PAGE_MAP[location.pathname] || 'home';

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/cms/sections/${pageId}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setSections(data.filter(s => s.is_visible !== false).sort((a, b) => (a.order || 0) - (b.order || 0))))
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  }, [pageId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid={`cms-page-${pageId}`}>
      {sections.map((s, i) => <SectionRenderer key={s.section_id || i} section={s} />)}
    </div>
  );
}

function SectionRenderer({ section }) {
  const type = section.section_type;

  if (type === 'hero') return <HeroSection s={section} />;
  if (type === 'contact') return <ContactSection s={section} />;
  if (type === 'services') return <ServicesSection s={section} />;
  if (type === 'faq') return <FAQSection s={section} />;
  if (type === 'why_choose_us') return <WhyChooseSection s={section} />;
  if (type === 'testimonials') return <TestimonialsSection s={section} />;
  if (type === 'about_content') return <AboutSection s={section} />;
  if (type === 'stats') return <StatsSection s={section} />;
  if (type === 'cta') return <CTASection s={section} />;
  if (type === 'text' || type === 'policy') return <TextSection s={section} />;
  return null;
}

function HeroSection({ s }) {
  return (
    <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 text-center">
        {s.badge_text && <span className="inline-block bg-lime-400/20 text-lime-300 text-sm px-4 py-1 rounded-full mb-4 border border-lime-400/30">{s.badge_text}</span>}
        <h1 className="text-3xl md:text-5xl font-bold mb-4">{s.title}</h1>
        {s.subtitle && <p className="text-lg md:text-xl text-blue-200 mb-6 max-w-2xl mx-auto">{s.subtitle}</p>}
        {s.content && <p className="text-blue-100/80 max-w-2xl mx-auto mb-8">{s.content}</p>}
        <div className="flex gap-4 justify-center flex-wrap">
          {s.cta_primary && <a href="tel:+919887068888" className="bg-lime-500 hover:bg-lime-600 text-gray-900 font-bold px-8 py-3 rounded-lg transition-colors">{s.cta_primary}</a>}
          {s.cta_secondary && <a href="https://wa.me/919887068888" className="border-2 border-white/30 hover:bg-white/10 px-8 py-3 rounded-lg transition-colors">{s.cta_secondary}</a>}
        </div>
        {s.stats?.length > 0 && (
          <div className="flex gap-8 justify-center mt-12 flex-wrap">
            {s.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-lime-400">{stat.value}</div>
                <div className="text-sm text-blue-200 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ContactSection({ s }) {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        {s.title && <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-2">{s.title}</h2>}
        {s.subtitle && <p className="text-center text-blue-600 mb-8">{s.subtitle}</p>}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {s.phone && (
            <a href={`tel:${s.phone.replace(/\s/g,'')}`} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
              <Phone className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Call Us</h3>
              <p className="text-blue-600 font-medium">{s.phone}</p>
            </a>
          )}
          {s.whatsapp && (
            <a href={`https://wa.me/${s.whatsapp.replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer" className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
              <MessageSquare className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
              <p className="text-green-600 font-medium">{s.whatsapp}</p>
            </a>
          )}
          {s.email && (
            <a href={`mailto:${s.email}`} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
              <Mail className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
              <p className="text-blue-600 font-medium">{s.email}</p>
            </a>
          )}
          {s.address && (
            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
              <MapPin className="w-8 h-8 text-red-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
              <p className="text-gray-600 text-sm">{s.address}</p>
            </div>
          )}
        </div>
        {s.map_embed && (
          <div className="mt-8 rounded-xl overflow-hidden shadow-sm">
            <iframe src={s.map_embed} width="100%" height="350" style={{ border: 0 }} loading="lazy" title="Location Map" />
          </div>
        )}
      </div>
    </section>
  );
}

function ServicesSection({ s }) {
  return (
    <section className="py-16">
      <div className="max-w-6xl mx-auto px-4">
        {s.title && <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-2">{s.title}</h2>}
        {s.subtitle && <p className="text-center text-blue-600 mb-8">{s.subtitle}</p>}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(s.services || []).map((srv, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              {srv.image && <img src={srv.image} alt={srv.title} className="w-full h-40 object-cover rounded-lg mb-4" />}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{srv.title}</h3>
              {srv.price && <p className="text-blue-600 font-bold mb-2">{srv.price}</p>}
              <p className="text-gray-600 text-sm">{srv.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection({ s }) {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4">
        {s.title && <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-2">{s.title}</h2>}
        {s.subtitle && <p className="text-center text-gray-500 mb-8">{s.subtitle}</p>}
        <div className="space-y-3">
          {(s.items || []).map((item, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50">
                <span className="font-medium text-gray-900">{item.question}</span>
                {openIndex === i ? <ChevronUp size={20} className="text-blue-600 flex-shrink-0" /> : <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />}
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5 text-gray-600 border-t border-gray-100 pt-3">{item.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChooseSection({ s }) {
  return (
    <section className="py-16">
      <div className="max-w-6xl mx-auto px-4">
        {s.title && <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-2">{s.title}</h2>}
        {s.subtitle && <p className="text-center text-gray-500 mb-8">{s.subtitle}</p>}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(s.reasons || []).map((r, i) => (
            <div key={i} className="bg-blue-50 rounded-xl p-6 text-center">
              <CheckCircle className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">{r.title}</h3>
              <p className="text-gray-600 text-sm">{r.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ s }) {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        {s.title && <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-2">{s.title}</h2>}
        {s.subtitle && <p className="text-center text-gray-500 mb-8">{s.subtitle}</p>}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(s.testimonials || []).map((t, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} size={16} className="text-yellow-400 fill-yellow-400" />)}</div>
              <p className="text-gray-600 mb-4 italic">"{t.content}"</p>
              <div className="flex items-center gap-3">
                {t.image && <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />}
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                  {t.location && <p className="text-gray-400 text-xs">{t.location}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSection({ s }) {
  return (
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4">
        {s.title && <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-6">{s.title}</h2>}
        {s.content && <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">{s.content}</p>}
        {(s.mission || s.vision) && (
          <div className="grid md:grid-cols-2 gap-6">
            {s.mission && (
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="font-bold text-blue-900 mb-2">Our Mission</h3>
                <p className="text-gray-700">{s.mission}</p>
              </div>
            )}
            {s.vision && (
              <div className="bg-green-50 p-6 rounded-xl">
                <h3 className="font-bold text-green-900 mb-2">Our Vision</h3>
                <p className="text-gray-700">{s.vision}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function StatsSection({ s }) {
  return (
    <section className="py-12 bg-blue-900 text-white">
      <div className="max-w-6xl mx-auto px-4">
        {s.title && <h2 className="text-xl font-bold text-center mb-6">{s.title}</h2>}
        <div className="flex flex-wrap justify-center gap-12">
          {(s.items || s.stats || []).map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-lime-400">{stat.value}</div>
              <div className="text-blue-200 text-sm mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection({ s }) {
  return (
    <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white text-center">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold mb-3">{s.title}</h2>
        {s.subtitle && <p className="text-blue-200 mb-6">{s.subtitle}</p>}
        {s.content && <p className="text-blue-100 mb-8">{s.content}</p>}
        {s.cta_text && (
          <a href={s.cta_link || 'tel:+919887068888'} className="inline-block bg-lime-500 hover:bg-lime-600 text-gray-900 font-bold px-8 py-3 rounded-lg transition-colors">
            {s.cta_text}
          </a>
        )}
      </div>
    </section>
  );
}

function TextSection({ s }) {
  return (
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4">
        {s.title && <h2 className="text-2xl font-bold text-gray-900 mb-4">{s.title}</h2>}
        {s.content && <div className="prose prose-gray max-w-none whitespace-pre-line text-gray-600">{s.content}</div>}
      </div>
    </section>
  );
}
